import ReactMarkdown from "react-markdown";
import { useState, useEffect, useRef, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from "react-router-dom";
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid,
  BarChart, Bar,
} from "recharts";
import "./App.css";
import VideoInterviewPage from './VideoInterviewPage';
import { backend } from "./index";

const AppContext = createContext();
function useApp() { return useContext(AppContext); }
const SECTIONS = [
  { id: "core_mcq", title: "Core Subjects MCQ", description: "CN, OS, OOPS, RDBMS", icon: "📚", color: "blue", total: 10 },
  { id: "dsa_mcq", title: "DSA MCQ", description: "Python & Java based DSA", icon: "🧠", color: "green", total: 10 },
  { id: "coding", title: "Coding Questions", description: "Problem solving & algorithms", icon: "💻", color: "purple", total: 5 },
  { id: "hr", title: "HR / Behavioral Interview", description: "Situational & behavioral questions", icon: "🤝", color: "orange", total: 8 },
];

const COLORS = { correct: "#22c55e", incorrect: "#ef4444", subjective: "#a78bfa", neutral: "#94a3b8" };
const BUBBLE_COLORS = ["#3b82f6", "#22c55e", "#f97316", "#a78bfa", "#ec4899", "#14b8a6"];


// ── Protected Route ────────────────────────────────────────────────────────
function ProtectedRoute({ children }) {
  const token = localStorage.getItem("token");
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

function LoginPage() {
  const { setCurrentUser, theme, setTheme } = useApp()
  const navigate = useNavigate();
  const [authStage, setAuthStage] = useState("login");
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({ name: "", email: "", password: "", college: "", branch: "" });

  const canvasRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) navigate("/home");
  }, []);

  // ── Ripple animation ──────────────────────────────────────────────────────
  useEffect(() => {
  const canvas = canvasRef.current;
  const ctx = canvas.getContext("2d");
  let W, H, animId;
  let particles = [];
  const mouse = { x: -999, y: -999 };

  function resize() {
    W = canvas.width = window.innerWidth;
    H = canvas.height = window.innerHeight;
    initParticles();
  }

  function initParticles() {
    particles = Array.from({ length: 120 }, () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      vx: (Math.random() - .5) * 1.2,
      vy: (Math.random() - .5) * 1.2,
      r: Math.random() * 2.5 + 1.5,
      hue: 180 + Math.random() * 80,
      pulse: Math.random() * Math.PI * 2,
    }));
  }

  function onMouseMove(e) {
    mouse.x = e.clientX;
    mouse.y = e.clientY;
  }

  function onMouseLeave() {
    mouse.x = -999;
    mouse.y = -999;
  }

  function loop() {
    ctx.fillStyle = theme === "light" 
    ? "rgba(255, 255, 255, 0.18)"  // Light trail
    : "rgba(6, 8, 24, 0.18)";      // Dark trail
    ctx.fillRect(0, 0, W, H);
    particles.forEach(p => {
      p.pulse += 0.04;
      p.x += p.vx;
      p.y += p.vy;

      // bounce off walls
      if (p.x < 0 || p.x > W) p.vx *= -1;
      if (p.y < 0 || p.y > H) p.vy *= -1;

      // mouse repel
      const mdx = p.x - mouse.x, mdy = p.y - mouse.y;
      const mdist = Math.sqrt(mdx * mdx + mdy * mdy);
      if (mdist < 130) {
        const force = (130 - mdist) / 130;
        p.vx += (mdx / mdist) * force * 0.6;
        p.vy += (mdy / mdist) * force * 0.6;
      }

      // speed cap
      const spd = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
      if (spd > 2.5) { p.vx = (p.vx / spd) * 2.5; p.vy = (p.vy / spd) * 2.5; }

      // glowing particle dot
      const pSize = p.r + Math.sin(p.pulse) * 0.8;
      const pg = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, pSize * 4);
      pg.addColorStop(0, `hsla(${p.hue}, 100%, 85%, 1)`);
      pg.addColorStop(0.4, `hsla(${p.hue}, 100%, 70%, 0.6)`);
      pg.addColorStop(1, `hsla(${p.hue}, 100%, 60%, 0)`);
      ctx.fillStyle = pg;
      ctx.beginPath();
      ctx.arc(p.x, p.y, pSize * 4, 0, Math.PI * 2);
      ctx.fill();

      // solid bright core
      ctx.fillStyle = theme === "light"
      ? `hsla(${p.hue}, 70%, 40%, 1)` // Darker particles for light mode
      : `hsla(${p.hue}, 100%, 92%, 1)`; // Brighter for dark mode
    ctx.beginPath();
    ctx.arc(p.x, p.y, pSize, 0, Math.PI * 2);
    ctx.fill();
    });

    // draw connections
    for (let i = 0; i < particles.length; i++) {
      const a = particles[i];

      // particle to particle
      for (let j = i + 1; j < particles.length; j++) {
        const b = particles[j];
        const dx = a.x - b.x, dy = a.y - b.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 130) {
          const alpha = (1 - dist / 130) * 0.85;
          const hue = (a.hue + b.hue) / 2;
          ctx.strokeStyle = `hsla(${hue}, 100%, 75%, ${alpha})`;
          ctx.lineWidth = (1 - dist / 130) * 2;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }

      // particle to mouse
      const mdx = a.x - mouse.x, mdy = a.y - mouse.y;
      const mdist = Math.sqrt(mdx * mdx + mdy * mdy);
      if (mdist < 200) {
        const alpha = (1 - mdist / 200) * 0.95;
        ctx.strokeStyle = `hsla(${a.hue}, 100%, 85%, ${alpha})`;
        ctx.lineWidth = (1 - mdist / 200) * 2.5;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(mouse.x, mouse.y);
        ctx.stroke();

        // cursor glow ring
        ctx.strokeStyle = `hsla(${a.hue}, 100%, 80%, ${alpha * 0.3})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(mouse.x, mouse.y, mdist * 0.15, 0, Math.PI * 2);
        ctx.stroke();
      }
    }

    // bright cursor dot
    if (mouse.x > 0 && mouse.x < W) {
      const cg = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, 24);
      cg.addColorStop(0, "rgba(255,255,255,1)");
      cg.addColorStop(0.3, "rgba(160,220,255,0.8)");
      cg.addColorStop(1, "rgba(100,180,255,0)");
      ctx.fillStyle = cg;
      ctx.beginPath();
      ctx.arc(mouse.x, mouse.y, 24, 0, Math.PI * 2);
      ctx.fill();
    }

    animId = requestAnimationFrame(loop);
  }

  resize();
  window.addEventListener("resize", resize);
  window.addEventListener("mousemove", onMouseMove);
  window.addEventListener("mouseleave", onMouseLeave);
  loop();

  return () => {
    cancelAnimationFrame(animId);
    window.removeEventListener("resize", resize);
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseleave", onMouseLeave);
  };
}, [theme]);

  const handleLogin = async () => {
    if (!loginForm.email || !loginForm.password) { setAuthError("Please fill all fields"); return; }
    setAuthLoading(true); setAuthError("");
    try {
      const res = await fetch(`${backend}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(loginForm),
      });
      const data = await res.json();
      if (!res.ok) { setAuthError(data.detail || "Login failed"); setAuthLoading(false); return; }
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      setCurrentUser(data.user);
      navigate("/home");
    } catch (error) {
      if (error.response) {
        const serverMessage = await error.response.text();
        setAuthError(`Server error: ${serverMessage}`);
      } else {
        setAuthError("Server error. Is the backend running?");
      }
    }
    setAuthLoading(false);
  };

  const handleRegister = async () => {
    const { name, email, password, college, branch } = registerForm;
    if (!name || !email || !password || !college || !branch) { setAuthError("Please fill all fields"); return; }
    if (password.length < 6) { setAuthError("Password must be at least 6 characters"); return; }
    setAuthLoading(true); setAuthError("");
    try {
      const res = await fetch(`${backend}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(registerForm),
      });
      const data = await res.json();
      if (!res.ok) { setAuthError(data.detail || "Registration failed"); setAuthLoading(false); return; }
      setAuthStage("login");
      setAuthError("");
      alert("Registration successful! Please login.");
    } catch (error) {
      if (error.response) {
        const serverMessage = await error.response.text();
        setAuthError(`Server error: ${serverMessage}`);
      } else {
        setAuthError("Server error. Is the backend running?");
      }
      setAuthLoading(false);
    }
  };

  return (
    <div className="auth-page" style={{ position: "relative", overflow: "hidden" }}>

      {/* ── Ripple canvas ── */}
      <canvas
        ref={canvasRef}
        style={{
          position: "fixed",
          inset: 0,
          width: "100%",
          height: "100%",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      {/* ── Everything else sits above the canvas ── */}
      <div style={{ position: "relative", zIndex: 1 }}>
        <button className="theme-toggle" onClick={() => setTheme(theme === "light" ? "dark" : "light")}>
          {theme === "light" ? "🌙 Dark Mode" : "☀️ Light Mode"}
        </button>

        <div className="auth-card">
          <div className="auth-logo">🎓</div>
          <h1 className="auth-title">PrepPilot</h1>
          <p className="auth-subtitle">
            {authStage === "login" ? "Welcome back! Login to continue." : "Create your account to get started."}
          </p>
          <div className="auth-tabs">
            <button className={`auth-tab ${authStage === "login" ? "auth-tab-active" : ""}`}
              onClick={() => { setAuthStage("login"); setAuthError(""); }}>Login</button>
            <button className={`auth-tab ${authStage === "register" ? "auth-tab-active" : ""}`}
              onClick={() => { setAuthStage("register"); setAuthError(""); }}>Register</button>
          </div>
          {authError && <div className="auth-error">{authError}</div>}
          {authStage === "login" ? (
            <div className="auth-form">
              <div className="auth-field">
                <label className="label">Email</label>
                <input className="auth-input" type="email" placeholder="you@email.com"
                  value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && handleLogin()} />
              </div>
              <div className="auth-field">
                <label className="label">Password</label>
                <input className="auth-input" type="password" placeholder="Enter password"
                  value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && handleLogin()} />
              </div>
              <button className="primary-btn" style={{ opacity: authLoading ? 0.6 : 1 }}
                onClick={handleLogin} disabled={authLoading}>
                {authLoading ? "Logging in..." : "Login →"}
              </button>
              <p className="auth-switch">Don't have an account?{" "}
                <span onClick={() => { setAuthStage("register"); setAuthError(""); }}>Register here</span>
              </p>
            </div>
          ) : (
            <div className="auth-form">
              <div className="auth-field">
                <label className="label">Full Name</label>
                <input className="auth-input" type="text" placeholder="Your full name"
                  value={registerForm.name} onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })} />
              </div>
              <div className="auth-field">
                <label className="label">Email</label>
                <input className="auth-input" type="email" placeholder="you@email.com"
                  value={registerForm.email} onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })} />
              </div>
              <div className="auth-field">
                <label className="label">Password</label>
                <input className="auth-input" type="password" placeholder="Min 6 characters"
                  value={registerForm.password} onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })} />
              </div>
              <div className="auth-field">
                <label className="label">College Name</label>
                <input className="auth-input" type="text" placeholder="Your college name"
                  value={registerForm.college} onChange={(e) => setRegisterForm({ ...registerForm, college: e.target.value })} />
              </div>
              <div className="auth-field">
                <label className="label">Branch / Department</label>
                <select className="auth-input" value={registerForm.branch}
                  onChange={(e) => setRegisterForm({ ...registerForm, branch: e.target.value })}>
                  <option value="">Select Branch</option>
                  <option>Computer Science</option>
                  <option>Information Technology</option>
                  <option>Electronics & Communication</option>
                  <option>Electrical Engineering</option>
                  <option>Mechanical Engineering</option>
                  <option>Civil Engineering</option>
                  <option>Other</option>
                </select>
              </div>
              <button className="primary-btn" style={{ opacity: authLoading ? 0.6 : 1 }}
                onClick={handleRegister} disabled={authLoading}>
                {authLoading ? "Registering..." : "Create Account →"}
              </button>
              <p className="auth-switch">Already have an account?{" "}
                <span onClick={() => { setAuthStage("login"); setAuthError(""); }}>Login here</span>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── HOME PAGE ──────────────────────────────────────────────────────────────
// ── HOME PAGE ──────────────────────────────────────────────────────────────
function HomePage() {
  const [resumePreview, setResumePreview] = useState('');
  const [resumeUploadedAt, setResumeUploadedAt] = useState('');
  const { currentUser, handleLogout, sectionProgress, theme, setTheme } = useApp();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("assessment");
  const [resumeFile, setResumeFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadDone, setUploadDone] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef(null);
// Auto-fetch resume from backend on tab switch to interview
  useEffect(() => {
  if (activeTab !== 'interview') return;
  const fetchStoredResume = async () => {
    try {
      const res = await fetch(backend+'/get-resume', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.exists) {
        setUploadDone(true);
        setResumeFile({ name: data.filename, size: 0 }); // mock file object for display
        setResumePreview(data.preview);
        setResumeUploadedAt(data.uploaded_at);
      }
    } catch (e) {
      console.log('No stored resume found');
    }
  };
  fetchStoredResume();
}, [activeTab]);
  // need useRef from React — add to your existing import at the top:
  // import { useState, useEffect, useRef, createContext, useContext } from "react";

  const token = localStorage.getItem("token");

  const validateAndSet = (file) => {
    setUploadError("");
    setUploadDone(false);
    const allowed = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!allowed.includes(file.type)) {
      setUploadError("Only PDF or DOCX files are supported.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError("File must be under 5 MB.");
      return;
    }
    setResumeFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) validateAndSet(file);
  };

  const handleUpload = async () => {
    if (!resumeFile) return;
    setUploading(true);
    setUploadError("");
    try {
      const form = new FormData();
      form.append("file", resumeFile);
      const res = await fetch(backend+'/upload-resume', {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload failed");
      setUploadDone(true);
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="container">
      {/* ── Top Bar (unchanged) */}
      <div className="home-topbar">
        <div>
          <h1 className="title">Online Assessment Platform</h1>
          <p className="subtitle">Welcome back, <strong>{currentUser?.name}</strong> 👋</p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button className="theme-toggle-sm" onClick={() => setTheme(theme === "light" ? "dark" : "light")}>
            {theme === "light" ? "🌙" : "☀️"}
          </button>
          <button className="logout-btn" onClick={handleLogout}>Logout</button>
        </div>
      </div>

      {/* ── Mode Tabs */}
      <div style={tabStyles.tabBar}>
        <button
          style={{ ...tabStyles.tab, ...(activeTab === "assessment" ? tabStyles.tabActiveBlue : tabStyles.tabInactive) }}
          onClick={() => setActiveTab("assessment")}
        >
          📝 Mock Assessment
        </button>
        <button
          style={{ ...tabStyles.tab, ...(activeTab === "interview" ? tabStyles.tabActiveGreen : tabStyles.tabInactive) }}
          onClick={() => setActiveTab("interview")}
        >
          🎥 Mock Interview
        </button>
      </div>

      {/* ── TAB: Mock Assessment */}
      {activeTab === "assessment" && (
        <div className="sections-grid">
          {SECTIONS.map((section) => {
            const progress = sectionProgress[section.id];
            const percent = Math.round((progress.done / progress.total) * 100);
            return (
              <div
                key={section.id}
                className={`section-card section-card-${section.color}`}
                onClick={() => navigate(`/select/${section.id}`)}
              >
                <div className="section-icon">{section.icon}</div>
                <div className="section-info">
                  <h3 className="section-title">{section.title}</h3>
                  <p className="section-desc">{section.description}</p>
                  <div className="progress-bar-wrap">
                    <div className="progress-bar-track">
                      <div className="progress-bar-fill" style={{ width: `${percent}%` }} />
                    </div>
                    <span className="progress-text">{progress.done}/{progress.total} done</span>
                  </div>
                </div>
                <div className="section-arrow">→</div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── TAB: Mock Interview */}
      {activeTab === "interview" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

          {/* Step 1 — Upload Resume */}
          <div style={tabStyles.stepCard}>
            <div style={tabStyles.stepBadge}>Step 1</div>
            <h3 style={tabStyles.stepTitle}>Upload Your Resume</h3>
            <p style={tabStyles.stepSubtitle}>
              The AI interviewer will personalise every question based on your actual projects, skills, and experience.
            </p>

            {/* Drop Zone */}
            <div
              style={{
                ...tabStyles.dropZone,
                ...(dragOver ? tabStyles.dropZoneActive : {}),
                ...(resumeFile ? tabStyles.dropZoneFilled : {}),
              }}
              onClick={() => fileInputRef.current.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx"
                style={{ display: "none" }}
                onChange={(e) => { if (e.target.files[0]) validateAndSet(e.target.files[0]); }}
              />
              {!resumeFile ? (
                <>
                  <div style={{ fontSize: 38, marginBottom: 10 }}>📄</div>
                  <div style={tabStyles.dropTitle}>
                    {dragOver ? "Drop it here!" : "Drag & drop your resume"}
                  </div>
                  <div style={tabStyles.dropHint}>or click to browse</div>
                  <div style={tabStyles.dropFormats}>PDF or DOCX · Max 5 MB</div>
                </>
              ) : (
                <>
    <div style={{ fontSize: 34, marginBottom: 8 }}>✅</div>
    <div style={{ fontWeight: 700, color: "#22c55e", marginBottom: 4 }}>
      {resumeFile.name}
    </div>
    {resumeUploadedAt && (
      <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
        Last uploaded: {new Date(resumeUploadedAt).toLocaleDateString()}
      </div>
    )}
    {resumePreview && (
      <div style={{ fontSize: 11, color: "#555", fontStyle: "italic", marginBottom: 10, maxWidth: 320, margin: "0 auto 10px" }}>
        "{resumePreview}"
      </div>
    )}
    <button
      style={tabStyles.changeBtn}
      onClick={(e) => {
        e.stopPropagation();
        setResumeFile(null);
        setUploadDone(false);
        setResumePreview('');
        setResumeUploadedAt('');
      }}
    >
      Replace resume
    </button>
  </>
)}
            </div>

            {uploadError && <div style={tabStyles.errorBox}>⚠️ {uploadError}</div>}

            {!uploadDone ? (
              <button
                className="primary-btn"
                style={{ opacity: resumeFile && !uploading ? 1 : 0.45, cursor: resumeFile && !uploading ? "pointer" : "not-allowed" }}
                onClick={handleUpload}
                disabled={!resumeFile || uploading}
              >
                {uploading ? "Uploading..." : "Upload Resume →"}
              </button>
            ) : (
              <div style={tabStyles.successBox}>
                ✅ Resume uploaded! Now choose a section below to start your interview.
              </div>
            )}
          </div>

          {/* Step 2 — Choose Section */}
          <div style={{ ...tabStyles.stepCard, opacity: uploadDone ? 1 : 0.45, pointerEvents: uploadDone ? "auto" : "none" }}>
            <div style={{ ...tabStyles.stepBadge, background: "rgba(34,197,94,0.12)", color: "#22c55e" }}>Step 2</div>
            <h3 style={tabStyles.stepTitle}>Choose Interview Section</h3>
            <p style={tabStyles.stepSubtitle}>
              {uploadDone ? "Pick a section — the AI bot will ask you questions live via camera & mic." : "Upload your resume first to unlock this step."}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 16 }}>
              {SECTIONS.map((section) => (
                <div
                  key={section.id}
                  className={`section-card section-card-${section.color}`}
                  style={{ cursor: uploadDone ? "pointer" : "not-allowed" }}
                  onClick={() => uploadDone && navigate(`/video-interview/${section.id}`)}
                >
                  <div className="section-icon">{section.icon}</div>
                  <div className="section-info">
                    <h3 className="section-title">{section.title}</h3>
                    <p className="section-desc">{section.description}</p>
                  </div>
                  <div className="section-arrow" style={{ color: "#22c55e", fontWeight: 700 }}>Start →</div>
                </div>
              ))}
            </div>
          </div>

        </div>
      )}
    </div>
  );
}

// Tab + interview UI styles (scoped, doesn't affect existing CSS)
const tabStyles = {
  tabBar: {
    display: "flex",
    gap: 8,
    marginBottom: 24,
    background: "var(--card-bg, #1a1928)",
    border: "1px solid var(--border, #2a2840)",
    borderRadius: 12,
    padding: 5,
  },
  tab: {
    flex: 1,
    padding: "11px 16px",
    border: "none",
    borderRadius: 9,
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
    letterSpacing: "0.2px",
  },
  tabActiveBlue: {
    background: "#3b82f6",
    color: "#fff",
    boxShadow: "0 2px 14px rgba(59,130,246,0.35)",
  },
  tabActiveGreen: {
    background: "#16a34a",
    color: "#fff",
    boxShadow: "0 2px 14px rgba(22,163,74,0.35)",
  },
  tabInactive: {
    background: "transparent",
    color: "var(--text-muted, #888)",
  },
  stepCard: {
    background: "var(--card-bg, #1e1d2e)",
    border: "1px solid var(--border, #2a2840)",
    borderRadius: 14,
    padding: "24px 28px",
    transition: "opacity 0.3s ease",
  },
  stepBadge: {
    display: "inline-block",
    background: "rgba(59,130,246,0.12)",
    color: "#3b82f6",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "1px",
    textTransform: "uppercase",
    padding: "3px 10px",
    borderRadius: 20,
    marginBottom: 10,
  },
  stepTitle: {
    margin: "0 0 6px",
    fontSize: 18,
    fontWeight: 700,
  },
  stepSubtitle: {
    margin: "0 0 20px",
    fontSize: 13,
    color: "var(--text-muted, #888)",
    lineHeight: 1.5,
  },
  dropZone: {
    border: "2px dashed var(--border, #2a2840)",
    borderRadius: 12,
    padding: "36px 24px",
    textAlign: "center",
    cursor: "pointer",
    transition: "all 0.2s ease",
    marginBottom: 16,
  },
  dropZoneActive: {
    border: "2px dashed #3b82f6",
    background: "rgba(59,130,246,0.05)",
  },
  dropZoneFilled: {
    border: "2px dashed #22c55e",
    background: "rgba(34,197,94,0.04)",
  },
  dropTitle: { fontSize: 15, fontWeight: 600, marginBottom: 5 },
  dropHint: { fontSize: 13, color: "#3b82f6", marginBottom: 6 },
  dropFormats: { fontSize: 12, color: "#666" },
  changeBtn: {
    background: "transparent",
    border: "1px solid #444",
    color: "#888",
    borderRadius: 6,
    padding: "5px 12px",
    fontSize: 12,
    cursor: "pointer",
  },
  errorBox: {
    color: "#ef4444",
    fontSize: 13,
    padding: "10px 14px",
    background: "rgba(239,68,68,0.08)",
    borderRadius: 8,
    border: "1px solid rgba(239,68,68,0.2)",
    marginBottom: 14,
  },
  successBox: {
    textAlign: "center",
    color: "#22c55e",
    fontWeight: 600,
    fontSize: 14,
    padding: 14,
    background: "rgba(34,197,94,0.08)",
    borderRadius: 10,
    border: "1px solid rgba(34,197,94,0.2)",
  },
};

// ── COMPANY SELECT PAGE ────────────────────────────────────────────────────
function SelectPage() {
  const { handleLogout, setActiveSection, setPages, setCurrentPage } = useApp();
  const navigate = useNavigate();
  const { sectionId } = useParams();
  const [company, setCompanyLocal] = useState("");
  const { setCompany } = useApp();

  const section = SECTIONS.find((s) => s.id === sectionId);

  useEffect(() => {
    if (section) setActiveSection(section);
  }, [sectionId]);

  if (!section) return <Navigate to="/home" replace />;

  const selectCompany = async () => {
    if (!company) { alert("Please select a company"); return; }
    const token = localStorage.getItem("token");
    await fetch(backend+'/select-company', {
      method: "POST",
      headers: { 
    "Content-Type": "application/json", 
    "Authorization": `Bearer ${token}` 
  },
    
      body: JSON.stringify({ company }),
    });
    setCompany(company);
    setPages([]);
    setCurrentPage(0);
    navigate(`/interview/${sectionId}`);
  };

  return (
    <div className="container">
      <button className="home-btn" onClick={() => navigate("/home")}>← Home</button>
      <div className={`section-banner section-banner-${section.color}`}>
        <span className="section-icon-lg">{section.icon}</span>
        <div>
          <h2 className="banner-title">{section.title}</h2>
          <p className="banner-desc">{section.description}</p>
        </div>
      </div>
      <div className="card">
        <label className="label">Select Company</label>
        <select className="select" onChange={(e) => setCompanyLocal(e.target.value)}>
          <option value="">Select Company</option>
          {["TCS","Infosys","Wipro","Accenture","Cognizant","Microsoft","Google","Amazon","Meta","Apple","IBM","Oracle","SAP","Adobe","Intel","Cisco","Capgemini","Deloitte","PwC","EY"].map((c, i) => (
            <option key={i} value={c}>{c}</option>
          ))}
        </select>
        <button className="primary-btn" onClick={selectCompany}>Start {section.title} →</button>
      </div>
    </div>
  );
}

// ── INTERVIEW PAGE ─────────────────────────────────────────────────────────
function InterviewPage() {
 const { activeSection, setActiveSection, company, pages, setPages, currentPage, setCurrentPage, sectionProgress, setSectionProgress, handleLogout, theme, setTheme } = useApp();
  const navigate = useNavigate();
  const { sectionId } = useParams();
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [mcqChoice, setMcqChoice] = useState(null);

  const section = SECTIONS.find((s) => s.id === sectionId);

  const authFetch = (url, options = {}) => {
    const token = localStorage.getItem("token");
    return fetch(url, {
      ...options,
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}`, ...(options.headers || {}) },
    });
  };

  useEffect(() => {
    if (section) setActiveSection(section);
    if (pages.length === 0) loadNextQuestion([]);
  }, []);

  const isMCQ = () => sectionId === "core_mcq" || sectionId === "dsa_mcq";

  const loadNextQuestion = async (existingPages) => {
    setLoading(true);
    try {
      const res = await authFetch(`${backend}/get-question`, {
        method: "POST",
        body: JSON.stringify({ section: sectionId }),
      });
      const data = await res.json();
      if (data.detail) { alert(data.detail); setLoading(false); return; }
      const newPages = [...existingPages, {
        question: data.question, options: data.options || null,
        correct: data.correct || null, explanation: data.explanation || null,
        topic: data.topic || "General", subtopic: data.subtopic || "General",
        answer: "", feedback: "", submitted: false, isCorrect: null,
      }];
      setPages(newPages);
      setCurrentPage(newPages.length - 1);
      setAnswer(""); setMcqChoice(null);
    } catch (e) { alert("Failed to load question."); console.error(e); }
    setLoading(false);
  };

  const submitAnswer = async () => {
    const finalAnswer = isMCQ() ? mcqChoice : answer;
    if (!finalAnswer) { alert("Please select or write an answer first"); return; }
    setLoading(true);
    const res = await authFetch(`${backend}/submit-answer`, {
      method: "POST",
      body: JSON.stringify({ answer: finalAnswer }),
    });
    const data = await res.json();
    setLoading(false);
    if (data.detail) { alert(data.detail); return; }
    const updatedPages = pages.map((p, i) =>
      i === currentPage
        ? { ...p, answer: finalAnswer, feedback: data.feedback, submitted: true, isCorrect: p.correct ? finalAnswer === p.correct : null }
        : p
    );
    setPages(updatedPages);
    setSectionProgress((prev) => ({
      ...prev,
      [sectionId]: { ...prev[sectionId], done: Math.min(prev[sectionId].done + 1, prev[sectionId].total) },
    }));
  };

  if (pages.length === 0) return <div className="container"><p className="loading-text">Loading question...</p></div>;

  const page = pages[currentPage];
  const isLastPage = currentPage === pages.length - 1;
  const progress = sectionProgress[sectionId];

  return (
    <div className="container">
      <div className="header">
        <div>
          <h2 className="header-title">{section?.icon} {section?.title}</h2>
          <p className="header-company">{company}</p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span className="badge">{progress.done}/{progress.total} done</span>
          <span className="badge badge-q">Q{currentPage + 1} of {pages.length}</span>
          <button className="theme-toggle-sm" onClick={() => setTheme(theme === "light" ? "dark" : "light")}>
  {theme === "light" ? "🌙" : "☀️"}
</button>
<button className="home-btn" onClick={() => navigate("/home")}>← Home</button>
          <button className="logout-btn" onClick={handleLogout}>Logout</button>
        </div>
      </div>

      <div className="tabs">
        {pages.map((p, i) => (
          <button key={i} onClick={() => { setCurrentPage(i); setAnswer(pages[i].answer); setMcqChoice(null); }}
            className={`tab ${i === currentPage ? "tab-active" : ""}`}>
            Q{i + 1} {p.submitted ? (p.isCorrect === true ? "✓" : p.isCorrect === false ? "✗" : "✓") : ""}
          </button>
        ))}
      </div>

      {page.topic && (
        <div className="topic-pill-row">
          <span className="topic-pill">📌 {page.topic}</span>
          {page.subtopic && <span className="topic-pill topic-pill-sub">🔹 {page.subtopic}</span>}
        </div>
      )}

      <div className="card">
        <div className="question-label">Question {currentPage + 1}</div>
        <div className="question-box"><ReactMarkdown>{page.question}</ReactMarkdown></div>
        {!page.submitted ? (
          <>
            {isMCQ() ? (
              <>
                <label className="label">Select your answer</label>
                <div className="mcq-options">
                  {Object.entries(page.options).map(([key, val]) => (
                    <button key={key} className={`mcq-btn ${mcqChoice === key ? "mcq-btn-active" : ""}`}
                      onClick={() => setMcqChoice(key)}>
                      <span className="mcq-key">{key}.</span> <ReactMarkdown>{val}</ReactMarkdown>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <label className="label">Your Answer</label>
                <textarea rows="6" className="textarea" placeholder="Type your answer here..."
                  value={isLastPage ? answer : page.answer} onChange={(e) => setAnswer(e.target.value)} />
              </>
            )}
            <button className="primary-btn" style={{ opacity: loading ? 0.6 : 1 }}
              onClick={submitAnswer} disabled={loading}>
              {loading ? "Submitting..." : "Submit Answer"}
            </button>
          </>
        ) : (
          <>
            <label className="label">Your Answer</label>
            <div className={`answer-box ${page.correct ? (page.answer === page.correct ? "answer-box-correct" : "answer-box-wrong") : "answer-box-correct"}`}>
              <span className="answer-result-icon">{page.correct ? (page.answer === page.correct ? "✓" : "✗") : "✓"}</span>
              {page.answer}
              {page.correct && page.answer !== page.correct && <p className="correct-ans">Correct answer: {page.correct}</p>}
            </div>
            {page.explanation && <div className="explanation-box"><strong>Explanation:</strong> {page.explanation}</div>}
            <button className="submitted-btn" disabled>✓ Submitted</button>
          </>
        )}
      </div>

      {page.submitted && page.feedback && (
        <div className="card">
          <div className="feedback-label">AI Feedback</div>
          <div className="feedback-box"><ReactMarkdown>{page.feedback}</ReactMarkdown></div>
          {isLastPage && (
            pages.length < sectionProgress[sectionId].total ? (
              <button className="primary-btn" style={{ opacity: loading ? 0.6 : 1 }}
                onClick={() => loadNextQuestion(pages)} disabled={loading}>
                {loading ? "Loading..." : "Next Question →"}
              </button>
            ) : (
              <button className="primary-btn" style={{ background: "#0f766e" }}
                onClick={() => navigate(`/summary/${sectionId}`)}>
                View Dashboard →
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}

// ── SUMMARY PAGE ───────────────────────────────────────────────────────────
function SummaryPage() {
  const { activeSection, company, currentUser, pages, handleLogout, sessionHistory, setSessionHistory, theme, setTheme } = useApp();
  const navigate = useNavigate();
  const { sectionId } = useParams();
  const [weakAnalysis, setWeakAnalysis] = useState("");
  const [analysisLoading, setAnalysisLoading] = useState(false);

  const section = SECTIONS.find((s) => s.id === sectionId);

  const authFetch = (url, options = {}) => {
    const token = localStorage.getItem("token");
    return fetch(url, {
      ...options,
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}`, ...(options.headers || {}) },
    });
  };

  useEffect(() => {
    const correct = pages.filter((p) => p.isCorrect === true).length;
    const total = pages.filter((p) => p.isCorrect !== null).length;
    const score = total > 0 ? Math.round((correct / total) * 100) : 0;
    setSessionHistory((prev) => ({
      ...prev,
      [sectionId]: [...(prev[sectionId] || []), { attempt: (prev[sectionId]?.length || 0) + 1, score, correct, total }],
    }));
    const wrongItems = pages.filter((p) => p.isCorrect === false)
      .map((p) => ({ question: p.question, topic: p.topic, subtopic: p.subtopic, correct_answer: p.correct, student_answer: p.answer }));
    if (wrongItems.length > 0) {
      setAnalysisLoading(true);
      authFetch(`${backend}/analyze-weak-topics`, {
        method: "POST",
        body: JSON.stringify({ wrong_items: wrongItems }),
      }).then((r) => r.json()).then((d) => { setWeakAnalysis(d.analysis); setAnalysisLoading(false); });
    }
  }, []);

  const correct = pages.filter((p) => p.isCorrect === true).length;
  const incorrect = pages.filter((p) => p.isCorrect === false).length;
  const subjective = pages.filter((p) => p.isCorrect === null).length;
  const total = pages.length;
  const score = Math.round((correct / (total - subjective || 1)) * 100);

  const overallPie = [
    { name: "Correct", value: correct, color: COLORS.correct },
    { name: "Incorrect", value: incorrect, color: COLORS.incorrect },
    ...(subjective > 0 ? [{ name: "Subjective", value: subjective, color: COLORS.subjective }] : []),
  ].filter((d) => d.value > 0);

  const topicMap = {};
  pages.forEach((p) => {
    const key = p.topic || "General";
    if (!topicMap[key]) topicMap[key] = { correct: 0, incorrect: 0, subtopics: {} };
    if (p.isCorrect === true) topicMap[key].correct++;
    else if (p.isCorrect === false) {
      topicMap[key].incorrect++;
      const sub = p.subtopic || "General";
      topicMap[key].subtopics[sub] = (topicMap[key].subtopics[sub] || 0) + 1;
    }
  });

  const topicData = Object.entries(topicMap).map(([topic, d], i) => ({
    topic, correct: d.correct, incorrect: d.incorrect,
    percent: Math.round((d.correct / (d.correct + d.incorrect || 1)) * 100),
    weakSubtopics: Object.entries(d.subtopics).map(([sub, count]) => ({ sub, count })),
    color: BUBBLE_COLORS[i % BUBBLE_COLORS.length],
  }));

  const history = sessionHistory[sectionId] || [];
  const bubbleData = history.map((h) => ({ x: h.attempt, y: h.score, z: h.total * 10, name: `Attempt ${h.attempt}` }));
  const barData = topicData.map((t) => ({ topic: t.topic, correct: t.correct, incorrect: t.incorrect }));
  const strongAreas = topicData.filter((t) => t.percent >= 60);
  const weakAreas = topicData.filter((t) => t.percent < 60);

  return (
    <div className="container">
      <div className="dash-topbar">
        <div>
          <h1 className="dash-title">Section Summary</h1>
          <p className="dash-sub">{section?.icon} {section?.title} — {company}</p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
  <button className="theme-toggle-sm" onClick={() => setTheme(theme === "light" ? "dark" : "light")}>
    {theme === "light" ? "🌙" : "☀️"}
  </button>
  <button className="home-btn" onClick={() => navigate("/home")}>← Home</button>
  <button className="logout-btn" onClick={handleLogout}>Logout</button>
</div>
      </div>

      <div className="dash-filters">
        <div className="dash-filter-pill">📅 This Session</div>
        <div className="dash-filter-pill">👤 {currentUser?.name}</div>
        <div className="dash-filter-pill">🏫 {currentUser?.college}</div>
        <div className="dash-filter-pill">Section: {section?.title}</div>
      </div>

      <div className="dash-top-row">
        <div className="metric-card">
          <p className="metric-label">Total Questions</p>
          <p className="metric-value">{total}</p>
          <p className="metric-sub" style={{ color: COLORS.correct }}>↑ {score}% score</p>
        </div>
        <div className="metric-card">
          <p className="metric-label">Correct Answers</p>
          <p className="metric-value" style={{ color: COLORS.correct }}>{correct}</p>
          <p className="metric-sub" style={{ color: COLORS.correct }}>↑ {Math.round((correct / total) * 100)}%</p>
        </div>
        <div className="metric-card">
          <p className="metric-label">Incorrect Answers</p>
          <p className="metric-value" style={{ color: COLORS.incorrect }}>{incorrect}</p>
          <p className="metric-sub" style={{ color: COLORS.incorrect }}>↓ {Math.round((incorrect / total) * 100)}%</p>
        </div>
        <div className="metric-card metric-card-chart">
          <p className="metric-label">Performance Split</p>
          <ResponsiveContainer width="100%" height={140}>
            <PieChart>
              <Pie data={overallPie} cx="50%" cy="50%" innerRadius={35} outerRadius={55} dataKey="value">
                {overallPie.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Pie>
              <Tooltip formatter={(val, name) => [`${val} questions`, name]} />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="dash-bottom-row">
        <div className="dash-chart-card">
          <p className="chart-title">Progress Over Attempts</p>
          <p className="chart-sub">Score variation across sessions</p>
          {bubbleData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="x" name="Attempt" tick={{ fontSize: 11 }}
                  label={{ value: "Attempt #", position: "insideBottom", offset: -10, fontSize: 11 }} />
                <YAxis dataKey="y" name="Score" domain={[0, 100]} tick={{ fontSize: 11 }}
                  label={{ value: "Score %", angle: -90, position: "insideLeft", fontSize: 11 }} />
                <ZAxis dataKey="z" range={[60, 400]} />
                <Tooltip formatter={(val, name) => [name === "Score" ? `${val}%` : val, name]} />
                <Scatter data={bubbleData} fill="#3b82f6" fillOpacity={0.7} />
              </ScatterChart>
            </ResponsiveContainer>
          ) : (
            <div className="no-data-box">
              <p>Complete more sessions to see progress trends.</p>
              <p style={{ fontSize: 12, color: "#aaa", marginTop: 6 }}>This is your first attempt.</p>
            </div>
          )}
        </div>
        <div className="dash-chart-card">
          <p className="chart-title">Topic Performance</p>
          <p className="chart-sub">Correct vs Incorrect per topic</p>
          {barData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={barData} margin={{ top: 10, right: 10, bottom: 40, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="topic" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" interval={0} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="correct" name="Correct" fill={COLORS.correct} radius={[4, 4, 0, 0]} />
                <Bar dataKey="incorrect" name="Incorrect" fill={COLORS.incorrect} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="no-data-box"><p>No topic data available.</p></div>
          )}
        </div>
      </div>

      <div className="dash-bottom-row">
        {strongAreas.length > 0 && (
          <div className="dash-chart-card">
            <p className="chart-title" style={{ color: "#16a34a" }}>💪 Strong Areas</p>
            {strongAreas.map((t, i) => (
              <div key={i} className="area-row">
                <span className="area-name">{t.topic}</span>
                <div className="area-bar-track"><div className="area-bar-fill area-bar-strong" style={{ width: `${t.percent}%` }} /></div>
                <span className="area-percent">{t.percent}%</span>
              </div>
            ))}
          </div>
        )}
        {weakAreas.length > 0 && (
          <div className="dash-chart-card">
            <p className="chart-title" style={{ color: "#dc2626" }}>⚠️ Weak Areas</p>
            {weakAreas.map((t, i) => (
              <div key={i}>
                <div className="area-row">
                  <span className="area-name">{t.topic}</span>
                  <div className="area-bar-track"><div className="area-bar-fill area-bar-weak" style={{ width: `${t.percent}%` }} /></div>
                  <span className="area-percent">{t.percent}%</span>
                </div>
                {t.weakSubtopics.length > 0 && (
                  <div className="subtopic-tags">
                    {t.weakSubtopics.map((s, j) => <span key={j} className="subtopic-tag">⚠ {s.sub} ({s.count} wrong)</span>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="dash-chart-card" style={{ marginTop: 16 }}>
        <p className="chart-title" style={{ color: "#7c3aed" }}>🤖 AI Study Recommendations</p>
        {analysisLoading ? (
          <p className="loading-text" style={{ marginTop: 16 }}>Analyzing your weak areas...</p>
        ) : weakAnalysis ? (
          <div className="feedback-box" style={{ marginTop: 12 }}><ReactMarkdown>{weakAnalysis}</ReactMarkdown></div>
        ) : (
          <p style={{ color: "#888", fontSize: 14, marginTop: 8 }}>
            {incorrect === 0 ? "🎉 Perfect score! No weak areas detected." : "No analysis available."}
          </p>
        )}
      </div>

      <button className="primary-btn" style={{ marginTop: 20 }} onClick={() => navigate("/home")}>← Back to Home</button>
    </div>
  );
}

// ── ROOT APP ───────────────────────────────────────────────────────────────
function App() {
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");

  useEffect(() => {
    document.body.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const [currentUser, setCurrentUser] = useState(() => {
    const u = localStorage.getItem("user");
    return u ? JSON.parse(u) : null;
  });
  const [company, setCompany] = useState("");
  const [activeSection, setActiveSection] = useState(null);
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [sectionProgress, setSectionProgress] = useState(() =>
    Object.fromEntries(SECTIONS.map((s) => [s.id, { done: 0, total: s.total }]))
  );
  const [sessionHistory, setSessionHistory] = useState(() =>
    Object.fromEntries(SECTIONS.map((s) => [s.id, []]))
  );

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setCurrentUser(null);
    setPages([]);
    setCompany("");
    setActiveSection(null);
    window.location.href = "/login";
  };

  return (
    <AppContext.Provider value={{
      currentUser, setCurrentUser,
      company, setCompany,
      activeSection, setActiveSection,
      pages, setPages,
      currentPage, setCurrentPage,
      sectionProgress, setSectionProgress,
      sessionHistory, setSessionHistory,
      handleLogout,
      theme, setTheme,
    }}>
      <BrowserRouter>
        <Routes>
          <Route path="/video-interview/:sectionId" element={<ProtectedRoute><VideoInterviewPage /></ProtectedRoute>} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/home" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
          <Route path="/select/:sectionId" element={<ProtectedRoute><SelectPage /></ProtectedRoute>} />
          <Route path="/interview/:sectionId" element={<ProtectedRoute><InterviewPage /></ProtectedRoute>} />
          <Route path="/summary/:sectionId" element={<ProtectedRoute><SummaryPage /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    </AppContext.Provider>
  );
}

export default App;